import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import { FaLock, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

import logo from '../assets/logo.png';

const UpdatePassword = () => {
    const { updatePassword } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [message, setMessage] = useState('');
    const [sessionCheck, setSessionCheck] = useState('checking'); // checking, valid, invalid

    // Verify we have a session (hash fragment from email link)
    useEffect(() => {
        const checkSession = async () => {
            // Supabase automatically handles the hash fragment and sets the session
            // We just need to verify that a user is actually logged in (via the recovery link)
            const { data: { session } } = await supabase.auth.getSession();

            if (session) {
                setSessionCheck('valid');
            } else {
                // Double check hash just in case
                if (location.hash && location.hash.includes('type=recovery')) {
                    // Let Supabase process the hash
                    setSessionCheck('valid');
                } else {
                    setSessionCheck('invalid');
                }
            }
        };
        checkSession();
    }, [location]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setStatus('error');
            setMessage('Passwords do not match');
            return;
        }

        if (password.length < 6) {
            setStatus('error');
            setMessage('Password must be at least 6 characters');
            return;
        }

        setStatus('loading');
        setMessage('');

        try {
            const { error } = await updatePassword(password);
            if (error) throw error;

            setStatus('success');
            setMessage('Your password has been successfully updated.');

            // Redirect to login or dashboard after delay
            setTimeout(() => {
                navigate('/login');
            }, 3000);
        } catch (error) {
            setStatus('error');
            setMessage(error.message);
        }
    };

    if (sessionCheck === 'checking') {
        return (
            <div className="min-h-screen bg-white dark:bg-[#0a1220] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
        );
    }

    if (sessionCheck === 'invalid') {
        return (
            <div className="min-h-screen bg-white dark:bg-[#0a1220] flex flex-col justify-center py-12 px-6 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-md text-center">
                    <FaExclamationCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Invalid or Expired Link</h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        The password reset link is invalid or has expired. Please request a new one.
                    </p>
                    <Link to="/forgot-password" className="text-orange-600 hover:text-orange-500 font-medium">
                        Request new link
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-white dark:bg-[#0a1220] flex flex-col justify-start pt-24 sm:pt-32 px-6 lg:px-8 transition-colors duration-300">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <div className="flex justify-center mb-6">
                    <img
                        src={logo}
                        alt="DataHelix Logo"
                        className="h-12 w-auto drop-shadow-lg hover:scale-105 transition-transform"
                    />
                </div>
                <h2 className="text-center text-3xl font-extrabold text-gray-900 dark:text-white">
                    Set new password
                </h2>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
                <div className="bg-white dark:bg-[#162032] py-8 px-4 shadow-xl rounded-xl border border-gray-100 dark:border-gray-800 sm:px-10 transition-colors duration-300">
                    {status === 'success' ? (
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-4">
                                <FaCheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Password Updated!</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                Redirecting you to login...
                            </p>
                        </div>
                    ) : (
                        <form className="space-y-6" onSubmit={handleSubmit}>
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    New Password
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FaLock className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        required
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-[#0a1220] placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-colors duration-200"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>

                            <div>
                                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Confirm Password
                                </label>
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <FaLock className="h-4 w-4 text-gray-400" />
                                    </div>
                                    <input
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type="password"
                                        required
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-[#0a1220] placeholder-gray-500 text-gray-900 dark:text-white focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm transition-colors duration-200"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>

                            {status === 'error' && (
                                <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4 border border-red-200 dark:border-red-800">
                                    <div className="flex">
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-red-800 dark:text-red-300">
                                                Error updating password
                                            </h3>
                                            <div className="mt-2 text-sm text-red-700 dark:text-red-400">
                                                <p>{message}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div>
                                <button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${status === 'loading'
                                        ? 'bg-orange-400 cursor-not-allowed'
                                        : 'bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500'
                                        } transition-all duration-200`}
                                >
                                    {status === 'loading' ? 'Updating...' : 'Update Password'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default UpdatePassword;
